<template>
  <div id="purchase-orders-history" class="align-start fill-height">
    <i-overlay
      v-if="fetching && $vuetifyDisplay.smAndDown"
      text="Loading purchase orders..."
    />

    <v-row v-else class="g-container-large" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0" :ref="pageRef">{{ $route.meta.title }}</h2>

        <v-row>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="4" md="3" lg="2">
            <i-instructions
              instructionKey="reports/purchase-orders-history"
            ></i-instructions>
          </v-col>
        </v-row>

        <i-search-sort
          ref="searchSort"
          :loading="fetching"
          :routeName="$route.name"
          @searchQuery="searchQuery"
        />

        <template v-if="!fetching && purchaseOrders.length === 0">
          <i-empty-results
            buttonText="Back to Reports"
            buttonRouteName="reports"
            :title="emptyFilteredResultsText"
          />
        </template>

        <template v-else>
          <v-row>
            <v-col cols="12">
              <template v-if="$vuetifyDisplay.smAndDown">
                <v-col
                  v-for="item in purchaseOrders"
                  :key="item.id"
                  class="mt-0 pa-0"
                >
                  <v-card class="rounded-2 pa-4 pa-md-6 mb-6">
                    <v-row no-gutters>
                      <v-col
                        cols="12"
                        class="py-2 d-flex justify-center"
                        v-if="item.status === 'CANCELLED'"
                      >
                        <v-chip x-small class="mb-2 i-tag-warning"
                          ><strong>{{ item.status }}</strong></v-chip
                        >
                      </v-col>
                      <v-col
                        cols="12"
                        class="py-2 d-flex justify-center"
                        v-else-if="item.status === 'PROCESSED_OFFLINE'"
                      >
                        <v-chip x-small class="mb-2 i-tag-primary"
                          ><strong>OFFLINE</strong></v-chip
                        >
                      </v-col>
                      <v-col
                        cols="12"
                        class="py-2 d-flex justify-center"
                        v-if="item.retailerDeliveryStatus === 'REJECTED'"
                      >
                        <v-chip x-small class="mb-2 i-tag-error"
                          ><strong>NOT RECEIVED</strong></v-chip
                        >
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span
                          ><strong>{{ headers[1].text }}</strong></span
                        >
                      </v-col>
                      <v-col cols="6" class="pl-4 py-1">
                        <span
                          ><strong>{{
                            $filters.text(item.supplierOrderNumber)
                          }}</strong></span
                        >
                      </v-col>
                    </v-row>

                    <v-divider class="my-2"></v-divider>

                    <v-row no-gutters>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[2].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.formatDateTime12h(item.createdAt)
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>
                          {{ headers[3].text }}
                          <v-icon
                            class="i-tooltip-color pb-1"
                            small
                            v-tooltip="'Includes fees'"
                          >
                            mdi-help-circle-outline
                          </v-icon>
                        </span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.currency(displayOrderTotal(item.summary))
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[4].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <i-payment-type
                          v-if="item.paymentType"
                          :paymentType="item.paymentType"
                          :textOnly="true"
                        />
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[5].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{ $filters.text(item.store.name) }}</span>
                        <v-btn
                          v-if="item.supplierOrderQueryId"
                          icon
                          small
                          v-tooltip="{
                            content: 'Order Query',
                            visible: $vuetifyDisplay.mdAndDown
                              ? false
                              : undefined
                          }"
                          :to="{
                            name: 'order-query-details',
                            params: { id: item.supplierOrderQueryId }
                          }"
                        >
                          <v-icon small class="i-order-query-link"
                            >mdi-clock-alert-outline</v-icon
                          >
                        </v-btn>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[6].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.formatDateTime12h(
                            item.actualFinalConfirmationDate
                          )
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>{{ headers[7].text }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.formatDateTime12h(
                            item.deliveredDateEnteredDate
                          )
                        }}</span>
                      </v-col>
                      <template v-if="isServiceOrder(item)">
                        <v-col cols="6" class="py-1">
                          <span>COMPLETED SERVICE DATE</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span>{{
                            $filters.formatDate(serviceCompletedDate(item))
                          }}</span>
                        </v-col>
                      </template>
                      <v-col cols="6" class="py-1">
                        <span>POSTCODE</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.text(item.deliveryAddress.postCode)
                        }}</span>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <span>STATE</span>
                      </v-col>
                      <v-col cols="6" class="py-1 pl-4">
                        <span>{{
                          $filters.text(item.deliveryAddress.state)
                        }}</span>
                      </v-col>
                      <template v-if="isServiceOrder(item)">
                        <v-col cols="6" class="py-1">
                          <span>ORDER TYPE</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <v-chip color="secondary" small> SERVICE </v-chip>
                        </v-col>
                      </template>
                    </v-row>

                    <v-divider class="mt-3 mb-4"></v-divider>

                    <v-btn
                      rounded
                      class="i-btn-block-secondary g-full-width"
                      :to="{
                        name: 'purchase-order-details',
                        params: { id: item.id }
                      }"
                    >
                      <strong>View Details</strong>
                    </v-btn>

                    <div
                      class="text-center pt-2"
                      v-if="item.paymentTransactionExpectedPeriodEndDate"
                    >
                      <v-btn
                        class="g-full-width i-btn-block-primary mt-1"
                        :to="paymentTransactionsRouteTo(item)"
                      >
                        <span>Payment Transaction Details</span>
                        <v-icon color="white" class="ml-1"
                          >mdi-list-box-outline</v-icon
                        >
                      </v-btn>
                    </div>
                    <div
                      class="text-center pt-2"
                      v-if="item.status !== 'CANCELLED'"
                    >
                      <v-btn
                        class="g-full-width i-btn-block-primary mt-1"
                        :disabled="!item.confirmedPdfAvailable"
                        @click="fetchPDF(item.id)"
                      >
                        <span>Export Purchase Order</span>
                        <v-icon color="white" class="ml-1"
                          >mdi-file-pdf-box</v-icon
                        >
                      </v-btn>
                    </div>
                    <div
                      class="text-center pt-2"
                      v-if="item.status !== 'CANCELLED'"
                    >
                      <v-btn
                        class="g-full-width i-btn-block-primary mt-1"
                        :disabled="
                          !item.confirmedPdfAvailable || isServiceOrder(item)
                        "
                        @click="fetchPDF(item.id, true)"
                      >
                        <span>Export Delivery Note</span>
                        <v-icon color="white" class="ml-1"
                          >mdi-file-pdf-box</v-icon
                        >
                      </v-btn>
                    </div>
                    <div
                      class="text-center pt-2"
                      v-if="
                        item.status !== 'CANCELLED' &&
                        item.status !== 'PROCESSED_OFFLINE'
                      "
                    >
                      <v-btn
                        class="g-full-width i-btn-block-primary mt-1"
                        :disabled="!item.invoiceNumber"
                        @click="fetchInvoicePDF(item.id)"
                      >
                        <span>Export Fees Invoice</span>
                        <v-icon color="white" class="ml-1"
                          >mdi-file-pdf-box</v-icon
                        >
                      </v-btn>
                    </div>
                  </v-card>
                </v-col>
              </template>

              <template v-else>
                <v-data-table
                  class="mt-2 pt-2 elevation-1 i-data-table"
                  :headers="headers"
                  :items="purchaseOrders"
                  :items-per-page="-1"
                  :loading="fetching"
                  :no-data-text="emptyFilteredResultsText"
                  :no-results-text="emptyFilteredResultsText"
                  hide-default-footer
                >
                  <template
                    v-slot:[`header.summary.remittanceTotal.totalIncludingTax`]="{
                      header
                    }"
                  >
                    {{ header.text }}
                    <v-icon
                      class="i-tooltip-color pb-1"
                      small
                      v-tooltip="'Includes fees'"
                    >
                      mdi-help-circle-outline
                    </v-icon>
                  </template>

                  <template v-slot:[`header.supplierOrderNumber`]="{ header }">
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('supplierOrderNumber')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('supplierOrderNumber')" small>
                        {{
                          isSortAsc('supplierOrderNumber')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template v-slot:[`header.createdAt`]="{ header }">
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('createdAt')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('createdAt')" small>
                        {{
                          isSortAsc('createdAt')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template v-slot:[`header.store.name`]="{ header }">
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('store.name')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('store.name')" small>
                        {{
                          isSortAsc('store.name')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template v-slot:[`header.paymentType`]="{ header }">
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('paymentType')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('paymentType')" small>
                        {{
                          isSortAsc('paymentType')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template
                    v-slot:[`header.deliveryAddress.postCode`]="{ header }"
                  >
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('deliveryAddress.postCode')"
                    >
                      {{ header.text }}
                      <v-icon
                        v-if="isSortedBy('deliveryAddress.postCode')"
                        small
                      >
                        {{
                          isSortAsc('deliveryAddress.postCode')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template
                    v-slot:[`header.deliveryAddress.state`]="{ header }"
                  >
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('deliveryAddress.state')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('deliveryAddress.state')" small>
                        {{
                          isSortAsc('deliveryAddress.state')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template v-slot:[`item.status`]="{ item }">
                    <v-chip
                      small
                      class="mb-2 mr-2 i-tag-warning"
                      v-if="item.status === 'CANCELLED'"
                      >CANCELLED</v-chip
                    >
                    <v-chip
                      small
                      class="mb-2 mr-2 i-tag-primary"
                      v-else-if="item.status === 'PROCESSED_OFFLINE'"
                      >OFFLINE</v-chip
                    >
                    <v-chip
                      small
                      class="mb-2 mr-2 i-tag-error"
                      v-else-if="item.retailerDeliveryStatus === 'REJECTED'"
                      >NOT RECEIVED</v-chip
                    >
                  </template>

                  <template v-slot:[`item.supplierOrderNumber`]="{ item }">
                    <span>{{ $filters.text(item.supplierOrderNumber) }}</span>
                  </template>

                  <template v-slot:[`item.createdAt`]="{ item }">
                    <span>{{
                      $filters.formatDateTime12h(item.createdAt)
                    }}</span>
                  </template>

                  <template
                    v-slot:[`item.summary.remittanceTotal.totalIncludingTax`]="{
                      item
                    }"
                  >
                    <span>{{
                      $filters.currency(displayOrderTotal(item.summary))
                    }}</span>
                  </template>

                  <template v-slot:[`item.paymentType`]="{ item }">
                    <i-payment-type
                      v-if="item.paymentType"
                      :paymentType="item.paymentType"
                      :textOnly="true"
                    />
                  </template>

                  <template v-slot:[`item.store.name`]="{ item }">
                    <span>{{ $filters.text(item.store.name) }}</span>
                    <v-btn
                      v-if="item.supplierOrderQueryId"
                      icon
                      v-tooltip="{
                        content: 'Order Query',
                        visible: $vuetifyDisplay.mdAndDown ? false : undefined
                      }"
                      :to="{
                        name: 'order-query-details',
                        params: { id: item.supplierOrderQueryId }
                      }"
                    >
                      <v-icon class="i-order-query-link"
                        >mdi-clock-alert-outline</v-icon
                      >
                    </v-btn>
                  </template>

                  <template
                    v-slot:[`item.actualFinalConfirmationDate`]="{ item }"
                  >
                    <span>{{
                      $filters.formatDateTime12h(
                        item.actualFinalConfirmationDate
                      )
                    }}</span>
                  </template>

                  <template v-slot:[`item.deliveredDateEnteredDate`]="{ item }">
                    <span>{{
                      $filters.formatDateTime12h(item.deliveredDateEnteredDate)
                    }}</span>
                  </template>

                  <template v-slot:[`item.completedServiceDate`]="{ item }">
                    <span v-if="isServiceOrder(item)">{{
                      $filters.formatDate(serviceCompletedDate(item))
                    }}</span>
                  </template>

                  <template v-slot:[`item.deliveryAddress.postCode`]="{ item }">
                    <span>{{
                      $filters.text(item.deliveryAddress.postCode)
                    }}</span>
                  </template>

                  <template v-slot:[`item.deliveryAddress.state`]="{ item }">
                    <span>{{ $filters.text(item.deliveryAddress.state) }}</span>
                  </template>

                  <template v-slot:[`item.orderType`]="{ item }">
                    <v-chip v-if="isServiceOrder(item)" color="secondary" small>
                      SERVICE
                    </v-chip>
                  </template>

                  <template v-slot:[`item.details`]="{ item }">
                    <v-btn
                      class="i-btn-block-secondary g-full-width"
                      :to="{
                        name: 'purchase-order-details',
                        params: { id: item.id }
                      }"
                      rounded
                    >
                      View
                    </v-btn>
                  </template>

                  <template v-slot:[`item.actions`]="{ item }">
                    <v-btn
                      :disabled="!item.paymentTransactionExpectedPeriodEndDate"
                      icon
                      :to="paymentTransactionsRouteTo(item)"
                      v-tooltip="{
                        content: 'View related Payment Transaction details',
                        visible: $vuetifyDisplay.mdAndDown ? false : undefined
                      }"
                    >
                      <v-icon class="i-btn-payment-transactions"
                        >mdi-list-box-outline</v-icon
                      >
                    </v-btn>
                    <v-btn
                      :disabled="
                        item.status === 'CANCELLED' ||
                        !item.confirmedPdfAvailable
                      "
                      icon
                      v-tooltip="{
                        content: 'Purchase Order PDF',
                        visible: $vuetifyDisplay.mdAndDown ? false : undefined
                      }"
                      @click="fetchPDF(item.id)"
                    >
                      <v-icon class="i-btn-pdf mr-1">mdi-file-pdf-box</v-icon>
                    </v-btn>
                    <v-btn
                      :disabled="
                        item.status === 'CANCELLED' ||
                        !item.confirmedPdfAvailable ||
                        isServiceOrder(item)
                      "
                      icon
                      v-tooltip="{
                        content: 'Delivery Note PDF',
                        visible: $vuetifyDisplay.mdAndDown ? false : undefined
                      }"
                      @click="fetchPDF(item.id, true)"
                    >
                      <v-icon class="i-btn-pdf">mdi-truck-check</v-icon>
                    </v-btn>
                    <span
                      v-tooltip="{
                        html: `feesInvoicePdfToolTipContent-${item.id}`,
                        offset: 8,
                        visible: $vuetifyDisplay.mdAndDown ? false : undefined
                      }"
                    >
                      <v-btn
                        :disabled="
                          item.status === 'CANCELLED' ||
                          item.status === 'PROCESSED_OFFLINE' ||
                          !item.invoiceNumber
                        "
                        icon
                        @click="fetchInvoicePDF(item.id)"
                      >
                        <v-icon class="i-btn-pdf"
                          >mdi-receipt-text-check</v-icon
                        >
                      </v-btn>
                    </span>
                    <span :id="`feesInvoicePdfToolTipContent-${item.id}`">
                      <span v-if="item.status === 'CANCELLED'"
                        >Not applicable for a cancelled order</span
                      >
                      <span v-else-if="!item.invoiceNumber"
                        >Available once transaction week is Closed</span
                      >
                      <span v-else>Fees Invoice PDF</span>
                    </span>
                  </template>
                </v-data-table>
              </template>

              <i-paginator
                :currentPage="currentPage"
                :pageSize="pageSize"
                :pagination="pagination"
                @paginate="handlePagination"
              />
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  fetchDeliveredFinalOrders,
  fetchOrderPDF,
  fetchSupplierOrderInvoicePdfUrl
} from '@/services/supplierOrders'
import iOverlay from '@/components/Overlay'
import iPaginator from '@/components/Paginator'
import iSearchSort from '@/components/SearchSort'
import { openFile, routeToPaymentTransactions } from '@/utils/common'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'
import iPaymentType from '@/components/PaymentType'
import { pickSupplierCostTotal } from '@/utils/supplierAmountDisplay'

export default {
  components: {
    iPaymentType,
    iEmptyResults,
    iInstructions,
    iPaginator,
    iOverlay,
    iSearchSort
  },

  data() {
    return {
      currentPage: 1,
      emptyFilteredResultsText: 'No Purchase Orders found',
      fetching: true,
      fetchingInvoicePDF: false,
      pageSize: 10,
      pageRef: 'pageTitle',
      pagination: {
        total: 0,
        totalPages: 0
      },
      purchaseOrders: [],
      query: null
    }
  },

  computed: {
    ...mapGetters('tenant', [
      'isILR',
      'tenantCountryRegion',
      'usesAustralianTaxLabels'
    ]),

    headers() {
      return [
        {
          text: '',
          value: 'status',
          align: 'centre',
          width: '20',
          sortable: false
        },
        {
          text: 'PO NUMBER',
          value: 'supplierOrderNumber',
          align: 'start',
          width: '7%',
          sortable: false
        },
        {
          text: 'ORDER DATE & TIME',
          value: 'createdAt',
          align: 'start',
          width: '11%',
          sortable: false
        },
        {
          text: this.usesAustralianTaxLabels
            ? 'PO TOTAL\n(INC TAX)'
            : 'PO TOTAL',
          value: 'summary.remittanceTotal.totalIncludingTax',
          align: 'center',
          width: '8%',
          sortable: false
        },
        {
          text: 'PAYMENT TYPE',
          value: 'paymentType',
          align: 'center',
          sortable: false,
          width: 80
        },
        {
          text: this.isILR ? 'CUSTOMER' : 'RETAILER',
          value: 'store.name',
          sortable: false
        },
        {
          text: 'DRAFT PO CONFIRMED DATE',
          value: 'actualFinalConfirmationDate',
          align: 'start',
          width: '11%',
          sortable: false
        },
        {
          text: 'SUPPLIER FINALISED DATE',
          value: 'deliveredDateEnteredDate',
          align: 'start',
          width: '11%',
          sortable: false
        },
        {
          text: 'COMPLETED SERVICE DATE',
          value: 'completedServiceDate',
          align: 'center',
          width: '11%',
          sortable: false
        },
        {
          text: 'POSTCODE',
          value: 'deliveryAddress.postCode',
          align: 'center',
          width: '6%',
          sortable: false
        },
        {
          text: 'STATE',
          value: 'deliveryAddress.state',
          align: 'center',
          width: '6%',
          sortable: false
        },
        {
          text: 'ORDER TYPE',
          value: 'orderType',
          align: 'center',
          sortable: false,
          width: 110
        },
        {
          text: 'DETAILS',
          value: 'details',
          align: 'center',
          sortable: false,
          width: 160
        },
        {
          text: 'ACTIONS',
          value: 'actions',
          align: 'center',
          sortable: false,
          width: 165
        }
      ]
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    handleHeaderSort(value) {
      this.$refs.searchSort?.sortElements(value)
    },

    isSortedBy(value) {
      return this.$refs.searchSort?.sortBy === value
    },

    isSortAsc(value) {
      return !!this.$refs.searchSort?.sortByOptions.find(
        (option) => option.value === value
      )?.asc
    },

    displayOrderTotal(summary) {
      return pickSupplierCostTotal({
        tenantCountryRegion: this.tenantCountryRegion,
        cost: summary?.remittanceTotal
      })
    },

    fetchPDF(orderId, getDeliveryNote) {
      fetchOrderPDF(orderId, getDeliveryNote)
        .then((response) => {
          const file = new Blob([response], { type: 'application/pdf' })
          const fileURL = URL.createObjectURL(file)
          openFile(fileURL)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Invoice ${orderId} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
    },

    fetchInvoicePDF(orderId) {
      this.fetchingInvoicePDF = true
      fetchSupplierOrderInvoicePdfUrl(orderId)
        .then((response) => {
          openFile(response.url)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Order ${orderId} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.fetchingInvoicePDF = false
        })
    },

    fetchPurchaseHistory(currentPage, pageSize, query) {
      this.fetching = true

      // currentPage decreased by 1 as currentPage API starts from 0
      currentPage -= 1

      fetchDeliveredFinalOrders(currentPage, pageSize, query)
        .then((response) => {
          this.purchaseOrders = response.supplierOrders
          this.pagination = response.pagination
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Purchase orders could not be loaded. Please try again...'
          })
        })
        .finally(() => {
          this.fetching = false
        })
    },

    handlePagination(page) {
      this.currentPage = page
      this.fetchPurchaseHistory(this.currentPage, this.pageSize, this.query)
      if (this.$refs[this.pageRef])
        this.$refs[this.pageRef].scrollIntoView({ behavior: 'smooth' })
    },

    searchQuery(queryObject) {
      if (queryObject.restartPagination) this.currentPage = 1

      this.query = queryObject.query
      this.fetchPurchaseHistory(this.currentPage, this.pageSize, this.query)
    },

    paymentTransactionsRouteTo(purchaseOrder) {
      return routeToPaymentTransactions(
        purchaseOrder,
        purchaseOrder.supplierOrderNumber
      )
    },

    isServiceOrder(order) {
      return order?.supplierOrderItems?.some(
        (item) => item.productType === 'SERVICE'
      )
    },

    serviceCompletedDate(order) {
      const completedDates = (order?.supplierOrderItems || [])
        .map((item) => item.supplierCompletedServiceDate)
        .filter(Boolean)
        .sort()

      return completedDates.length
        ? completedDates[completedDates.length - 1]
        : null
    }
  },

  created() {
    this.fetchPurchaseHistory(this.currentPage, this.pageSize)
  }
}
</script>
